import { useState } from "react";
import { toast } from "sonner";
import { Route, Loader2, Play, ArrowDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { UploadZone } from "@/components/UploadZone";
import { analyzeCsv, getSampleCsv, simulateTraffic } from "@/lib/api";
import { cn } from "@/lib/utils";

export default function Traffic() {
  const [rules, setRules] = useState(null);
  const [filename, setFilename] = useState("");
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState({
    source: "10.0.0.5",
    destination: "8.8.8.8",
    protocol: "udp",
    port: "53",
  });
  const [result, setResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  const loadFile = async (file) => {
    setLoading(true);
    try {
      const data = await analyzeCsv(file);
      setRules(data.rules);
      setFilename(data.filename);
      toast.success(`Loaded ${data.rules.length} rules`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to parse CSV");
    } finally {
      setLoading(false);
    }
  };

  const loadSample = async () => {
    setLoading(true);
    try {
      const csv = await getSampleCsv();
      const file = new File([csv], "sample.csv", { type: "text/csv" });
      await loadFile(file);
    } finally {
      setLoading(false);
    }
  };

  const run = async () => {
    if (!rules) return;
    setSimLoading(true);
    try {
      const res = await simulateTraffic(rules, q.source, q.destination, q.protocol, q.port);
      setResult(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Simulation failed");
    } finally {
      setSimLoading(false);
    }
  };

  const verdictColor = (v) => {
    const u = (v || "").toUpperCase();
    if (u === "ALLOW" || u === "PERMIT" || u === "ACCEPT") return "text-emerald-400";
    if (u === "DENY" || u === "DROP" || u === "REJECT" || u === "IMPLICIT_DENY")
      return "text-red-400";
    return "text-slate-200";
  };

  return (
    <div className="relative">
      <div className="grid-bg absolute inset-0 pointer-events-none" />
      <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-10 py-10">
        <header className="pb-8 border-b border-[#1e293b]">
          <div className="flex items-center gap-2 text-blue-400 font-mono text-xs tracking-[0.3em] uppercase mb-3">
            <Route className="h-3.5 w-3.5" />
            <span>traffic_sanity_check</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-50">
            Trace a <span className="text-blue-400">5-tuple</span>
          </h1>
          <p className="mt-4 text-slate-400 text-sm max-w-2xl">
            Paste any (source, destination, protocol, port) and watch it walk through your ruleset,
            top to bottom. See which rule wins and why every other rule missed.
          </p>
        </header>

        {!rules ? (
          <section className="pt-8">
            <UploadZone onFile={loadFile} onLoadSample={loadSample} loading={loading} />
          </section>
        ) : (
          <section className="pt-8 space-y-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500">
              Ruleset: <span className="text-slate-300">{filename}</span> · {rules.length} rules
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 bg-[#0f172a] border border-[#1e293b] rounded-sm p-5">
              {[
                { k: "source", label: "Source IP", placeholder: "10.0.0.5" },
                { k: "destination", label: "Destination", placeholder: "8.8.8.8 or dns.google" },
                { k: "protocol", label: "Protocol", placeholder: "tcp / udp / icmp" },
                { k: "port", label: "Port", placeholder: "53" },
              ].map((f) => (
                <div key={f.k}>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-mono text-slate-400">
                    {f.label}
                  </Label>
                  <Input
                    data-testid={`sim-${f.k}-input`}
                    value={q[f.k]}
                    placeholder={f.placeholder}
                    onChange={(e) => setQ({ ...q, [f.k]: e.target.value })}
                    className="mt-2 bg-[#020617] border-[#1e293b] rounded-sm font-mono text-sm"
                  />
                </div>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <Button
                data-testid="sim-run-btn"
                onClick={run}
                disabled={simLoading}
                className="rounded-sm bg-blue-500 hover:bg-blue-400 text-white"
              >
                {simLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                Run trace
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setRules(null);
                  setResult(null);
                }}
                className="rounded-sm text-slate-400 hover:text-slate-100 hover:bg-[#1e293b]"
              >
                Change ruleset
              </Button>
            </div>

            {result && (
              <div className="space-y-5 animate-fade-up">
                <div
                  data-testid="sim-verdict"
                  className="bg-[#0f172a] border border-[#1e293b] rounded-sm p-5 flex items-center gap-4"
                >
                  <div className="text-[10px] tracking-[0.25em] uppercase font-mono text-slate-400">
                    Verdict
                  </div>
                  <div className={cn("font-mono text-3xl font-semibold", verdictColor(result.verdict))}>
                    {result.verdict}
                  </div>
                  {result.match && (
                    <div className="ml-auto text-xs font-mono text-slate-400">
                      matched by rule{" "}
                      <span className="text-slate-100">#{result.match.rule_id}</span>
                      <span className="text-slate-500"> ({result.match.name})</span>
                    </div>
                  )}
                </div>

                <div className="bg-[#0f172a] border border-[#1e293b] rounded-sm p-5">
                  <div className="text-[10px] tracking-[0.25em] uppercase font-mono text-slate-400 mb-4">
                    Evaluation trace ({result.trace.length} steps)
                  </div>
                  <ol className="space-y-2">
                    {result.trace.map((s, idx) => (
                      <li
                        key={idx}
                        data-testid={`sim-step-${s.rule_id}`}
                        className={cn(
                          "flex items-start gap-3 border rounded-sm px-3 py-2 font-mono text-xs",
                          s.matched
                            ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-100"
                            : "border-[#1e293b] text-slate-400"
                        )}
                      >
                        <span className="text-slate-600 w-6 text-right">{s.index + 1}</span>
                        <span className={cn("min-w-[64px]", s.matched ? "text-emerald-300" : "text-slate-300")}>
                          #{s.rule_id}
                        </span>
                        <span className="min-w-[140px] text-slate-400">{s.name}</span>
                        <span className={cn("min-w-[70px]", verdictColor(s.action))}>
                          {s.action?.toUpperCase()}
                        </span>
                        <span className="flex-1">
                          {s.matched ? (
                            <span className="text-emerald-300">✓ MATCH — traffic wins here</span>
                          ) : (
                            s.reasons.map((r, i) => (
                              <span key={i} className="text-slate-400">
                                × {r}
                                {i < s.reasons.length - 1 && "  ·  "}
                              </span>
                            ))
                          )}
                        </span>
                        {!s.matched && idx < result.trace.length - 1 && (
                          <ArrowDown className="h-3 w-3 text-slate-700 mt-1" />
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
